// src/app/signin/signin-content.tsx
'use client'

import { FormEvent, useState } from 'react'
import Link from 'next/link'
import { signIn } from 'next-auth/react'
import { useRouter } from 'next/navigation'
import { signinReasonCopy } from '@/lib/auth/signin-redirect'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { SignInButton } from '@/components/auth/SignInButton'

/**
 * SignInContent — the actual sign-in UI.
 *
 * Until this page existed the /signin surface only exposed Google.
 * Credentials sign-up was wired through /signup but there was no way
 * back in for those users — they were effectively locked out. This
 * component restores symmetry: Google up top, email/password below,
 * optional 2FA field for accounts that have enrolled, forgot-password
 * link to recover, and a "resend verification email" escape hatch
 * for users whose verification email never arrived (the credentials
 * provider returns null for unverified accounts, indistinguishable
 * from wrong-password — by design for enumeration safety — so we
 * surface the recovery path universally on failure).
 *
 * Query params honored:
 *   callbackUrl    where to land after successful sign-in
 *   reset=1        banner: "password updated, please sign in"
 *   verify=ok      banner: "email verified, please sign in"
 *                  (set by /api/auth/verify on a successful click-through;
 *                  the verified=1 alias is still accepted for back-compat
 *                  with any in-flight email links or external references)
 *   error=...      NextAuth's error code (forwarded from /api/auth)
 */
type Props = {
  searchParams?: {
    callbackUrl?: string
    reset?: string
    verify?: string
    verified?: string
    error?: string
    /** Why the visitor was sent here. A KEY, never copy — see
     *  signin-redirect.ts for why the text cannot come from the URL. */
    reason?: string
  }
}

export default function SignInContent({ searchParams }: Props) {
  const callbackUrl = searchParams?.callbackUrl ?? '/dashboard'
  const showResetBanner = searchParams?.reset === '1'
  // Accept either `verify=ok` (what /api/auth/verify actually sends) or
  // the legacy `verified=1`. Pre-fix only `verified=1` was checked, so
  // the post-verification banner was silent — Visakh clicked the verify
  // link, was redirected to /signin?verify=ok, and saw no confirmation
  // that his email had been verified.
  const showVerifiedBanner = searchParams?.verify === 'ok' || searchParams?.verified === '1'
  const initialError = searchParams?.error ?? null
  // Null for an absent or unrecognised key, so a crafted ?reason= renders
  // nothing rather than attacker-chosen copy beside a real password field.
  const reasonCopy = signinReasonCopy(searchParams?.reason)

  const router = useRouter()
  const [pending, setPending] = useState(false)
  // 2FA is opt-in (enrolled in Settings) — hide the code field behind a
  // disclosure so the ~all users who don't use it aren't asked for a code they
  // don't have. Those who enabled it click "Have a 2FA code?" to reveal it.
  const [showTotp, setShowTotp] = useState(false)
  const [error, setError] = useState<string | null>(
    initialError === 'CredentialsSignin'
      ? 'Email or password was incorrect.'
      : initialError === 'Configuration'
        ? 'Sign-in is not configured. Please contact support.'
        : initialError
          ? "Couldn't sign you in. Please try again."
          : null,
  )

  // Resend-verification state. Visible on the error banner; one click
  // fires POST /api/auth/resend-verification with the email the user
  // just typed (we keep it in state for this purpose). The endpoint
  // is leak-safe — same response for "no user", "already verified",
  // and the happy path — so offering this link universally on signin
  // failure doesn't reveal anything.
  const [lastTypedEmail, setLastTypedEmail] = useState('')
  const [resendPending, setResendPending] = useState(false)
  const [resendMessage, setResendMessage] = useState<string | null>(null)

  async function onCredentialsSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault()
    setError(null)
    setResendMessage(null)
    setPending(true)

    // form.elements.namedItem (vs the form.email shorthand) is the
    // form-element-name access path jsdom supports — the shorthand
    // returns undefined under jsdom, which made unit-testing the
    // submit handler impossible. Same semantics in real browsers.
    const form = e.currentTarget
    const email = (form.elements.namedItem('email') as HTMLInputElement).value.trim()
    const password = (form.elements.namedItem('password') as HTMLInputElement).value
    const totpCode =
      (form.elements.namedItem('totpCode') as HTMLInputElement | null)?.value.trim() ?? ''

    setLastTypedEmail(email)

    try {
      // `redirect: false` so we can show inline errors. On success
      // we manually navigate to callbackUrl.
      const result = await signIn('credentials', {
        email,
        password,
        totpCode: totpCode || undefined,
        redirect: false,
        callbackUrl,
      })

      if (result?.ok && !result.error) {
        // Two-step navigation: router.push moves the URL; router.refresh
        // re-fetches every server component on the new route (including
        // the root layout's <SiteNav/>, which calls getServerSession).
        //
        // Without router.refresh the layout's nav was rendered from the
        // pre-signin request (when getServerSession returned null) and
        // stuck in cache — the destination page would show the user's
        // name in its body, but the header still said "Sign in." Visakh
        // hit this and reported: "it recognized me and called Visakh,
        // but the status on top right still said 'Sign in' instead of
        // 'Sign Out'." router.refresh forces the layout-level
        // re-fetch so the new session cookie is read everywhere.
        //
        // The Google OAuth path uses redirect: true (full page reload)
        // so it doesn't have this problem — but credentials with
        // redirect: false requires the explicit refresh.
        router.push(result.url ?? callbackUrl)
        router.refresh()
        return
      }

      // NextAuth returns the same `CredentialsSignin` error for every
      // authorize() null-return — wrong password, no user, unverified,
      // missing 2FA, wrong 2FA. We can't (and shouldn't) tell them
      // apart in the message; the resend-verification link handles
      // the unverified case without enumeration.
      setError('Email or password was incorrect.')
    } catch (err) {
      console.error('[signin] credentials submit failed', err)
      setError('Network error. Please try again.')
    } finally {
      setPending(false)
    }
  }

  async function onResendVerification() {
    if (!lastTypedEmail) {
      setResendMessage('Enter your email above first, then try again.')
      return
    }
    setResendPending(true)
    setResendMessage(null)
    try {
      const res = await fetch('/api/auth/resend-verification', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: lastTypedEmail }),
      })
      const data = (await res.json().catch(() => ({}))) as {
        message?: string
      }
      // The endpoint is leak-safe and returns a generic message.
      setResendMessage(
        data.message ??
          "If we have an unverified account for that email, we've sent a new verification link.",
      )
    } catch {
      setResendMessage('Network error. Please try again.')
    } finally {
      setResendPending(false)
    }
  }

  return (
    <main className="min-h-dvh bg-background text-foreground">
      <div className="mx-auto max-w-md px-4 py-12">
        <Card className="border-border/70 bg-card/60">
          <CardHeader>
            <CardTitle className="text-foreground">Sign in</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Why you were sent here — shown above the success banners
                because it is the reason this page is open at all. Neutral
                styling, not green: nothing good has happened yet. */}
            {reasonCopy && (
              <div
                role="status"
                className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground"
              >
                {reasonCopy}
              </div>
            )}

            {/* Success banners */}
            {showResetBanner && (
              <div
                role="status"
                className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300"
              >
                Password updated. Sign in with your new password.
              </div>
            )}
            {showVerifiedBanner && (
              <div
                role="status"
                className="rounded-md border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700 dark:text-emerald-300"
              >
                Email verified. You can sign in now.
              </div>
            )}

            {/* Google */}
            <SignInButton callbackUrl={callbackUrl} />

            <div className="relative text-center text-muted-foreground">
              <span className="relative z-10 bg-card/60 px-2">or</span>
              <div className="absolute left-0 right-0 top-1/2 h-px bg-border" />
            </div>

            {/* Email + password */}
            <form className="space-y-3" onSubmit={onCredentialsSubmit}>
              <div>
                <label htmlFor="signin-email" className="block text-sm text-muted-foreground">
                  Email
                </label>
                <input
                  id="signin-email"
                  name="email"
                  type="email"
                  required
                  autoComplete="email"
                  className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>
              <div>
                <div className="flex items-center justify-between">
                  <label htmlFor="signin-password" className="block text-sm text-muted-foreground">
                    Password
                  </label>
                  <Link
                    href="/forgot-password"
                    className="text-xs text-muted-foreground underline decoration-slate-500 hover:text-foreground"
                  >
                    Forgot?
                  </Link>
                </div>
                <input
                  id="signin-password"
                  name="password"
                  type="password"
                  required
                  autoComplete="current-password"
                  className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
              </div>

              {/* Optional 2FA, hidden behind a disclosure. We can't reveal it
                  automatically (the credentials provider returns the same
                  generic error for missing 2FA as for a wrong password, so
                  detecting enrollment would enumerate which accounts use 2FA) —
                  so users who enabled it in Settings click to show the field.
                  When collapsed, `totpCode` simply isn't in the form. */}
              {showTotp ? (
                <div>
                  <label htmlFor="signin-totp" className="block text-sm text-muted-foreground">
                    2FA code
                  </label>
                  <input
                    id="signin-totp"
                    name="totpCode"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    pattern="[0-9]*"
                    maxLength={10}
                    placeholder="6-digit code"
                    autoFocus
                    className="mt-1 w-full rounded-md border border-border bg-card/40 px-3 py-2 text-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                  />
                </div>
              ) : (
                <button
                  type="button"
                  onClick={() => setShowTotp(true)}
                  className="self-start text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                >
                  Have a 2FA code?
                </button>
              )}

              <Button type="submit" disabled={pending} className="w-full bg-brand text-on-brand">
                {pending ? 'Signing in...' : 'Sign in'}
              </Button>

              {error && (
                <div
                  role="alert"
                  className="rounded-md border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-700 dark:text-red-300"
                >
                  <p>{error}</p>
                  {/* Resend-verification escape hatch. Same response
                      for all internal cases; safe to offer to anyone
                      who failed signin. */}
                  <button
                    type="button"
                    onClick={onResendVerification}
                    disabled={resendPending}
                    className="mt-2 text-xs underline decoration-red-400/60 hover:decoration-red-500 disabled:opacity-60"
                  >
                    {resendPending ? 'Sending...' : "Didn't get the verification email? Resend it"}
                  </button>
                  {resendMessage && (
                    <p className="mt-2 text-xs text-muted-foreground">{resendMessage}</p>
                  )}
                </div>
              )}

              <p className="text-xs text-muted-foreground">
                Don&apos;t have an account?{' '}
                <Link className="underline decoration-slate-500" href="/signup">
                  Sign up
                </Link>
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </main>
  )
}
